import {
    Box,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextareaAutosize,
    Typography
} from '@mui/material';
import React, { Dispatch, useState } from 'react';
import { PhysicianReviewModel } from '../../lib/models/PhysicianReviewModel';
import t from '../../theme';
import {
    headerRowStyle,
    radioRowStyle,
    medicalDeterminationRationaleRowStyle,
    medicalDeterminationRationaleTextStyle
} from '../../style';
import { useScreenSmallerThan } from '../../lib/hooks/breakpoint-hooks';
import { CaseStages } from '../../lib/constants/misc';

export const MedicalReviewDeterminationConst = {
    Approved: 'Approved',
    PartiallyApproved: 'Partially Approved',
    Denied: 'Denied'
};

interface PhysicianReviewProps {
    physicianReview: PhysicianReviewModel | undefined;
    label: string;
    caseStage: string;
    setAnyChangeMade: Dispatch<boolean>;
}

const PhysicianReviewSecondPartSpanish = ({
    physicianReview,
    caseStage,
    setAnyChangeMade
}: PhysicianReviewProps) => {
    const isSmallerThanSmall = useScreenSmallerThan('sm');
    const [medicalReviewDeterminationValue, setMedicalReviewDeterminationValue] = useState(
        physicianReview?.medicalReviewDetermination ?? ''
    );

    const [reasonMedicalReviewDeterminationValue, setReasonMedicalReviewDeterminationValue] =
        useState(physicianReview?.reasonMedicalReviewDetermination ?? '');
    const [
        reasonMedicalReviewDeterminationSpanishValue,
        setReasonMedicalReviewDeterminationSpanishValue
    ] = useState(physicianReview?.reasonMedicalReviewDeterminationSpanish ?? '');

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const handleReasonMedicalReviewDeterminationSpanish = (event: any) => {
        setAnyChangeMade(true);
        if (event.nativeEvent.inputType === 'insertLineBreak') {
            setReasonMedicalReviewDeterminationSpanishValue(reasonMedicalReviewDeterminationValue);
        } else {
            setReasonMedicalReviewDeterminationSpanishValue(event.target.value);
        }
    };

    const handleReasonMedicalReviewDetermination = (event: any) => {
        setAnyChangeMade(true);
        if (event.nativeEvent.inputType === 'insertLineBreak') {
            setReasonMedicalReviewDeterminationValue(reasonMedicalReviewDeterminationValue);
        } else {
            setReasonMedicalReviewDeterminationValue(event.target.value);
        }
    };

    React.useEffect(() => {
        if (physicianReview) {
            physicianReview.medicalReviewDetermination = medicalReviewDeterminationValue;
            physicianReview.reasonMedicalReviewDetermination =
                reasonMedicalReviewDeterminationValue;
            physicianReview.reasonMedicalReviewDeterminationSpanish =
                reasonMedicalReviewDeterminationSpanishValue.trim();
        }
    }, [
        medicalReviewDeterminationValue,
        reasonMedicalReviewDeterminationValue,
        reasonMedicalReviewDeterminationSpanishValue,
        physicianReview
    ]);

    return (
        <Box
            sx={{
                margin: !isSmallerThanSmall ? '0 1.25rem 0.875rem' : '0',
                padding: !isSmallerThanSmall ? '0 0.625rem 0.625rem' : '0.625rem 0.625rem',
                background: 'white',
                borderRadius: !isSmallerThanSmall ? '0.625rem' : '0'
            }}>
            <Box sx={isSmallerThanSmall ? { padding: '0' } : { paddingTop: '0.625rem' }}>
                <Box sx={headerRowStyle}>
                    <Typography variant="h5">Medical Determination Rationale</Typography>
                </Box>
                {caseStage === CaseStages.Initial ? (
                    <Box
                        sx={
                            !isSmallerThanSmall
                                ? radioRowStyle
                                : {
                                      padding: 0,
                                      borderStyle: 'solid',
                                      borderWidth: '0 0.063rem',
                                      borderColor: t.palette.globalSwitch.disabledBg
                                  }
                        }>
                        <RadioGroup
                            sx={
                                !isSmallerThanSmall
                                    ? { flexDirection: 'row' }
                                    : { flexDirection: 'column' }
                            }>
                            <Box
                                sx={
                                    !isSmallerThanSmall
                                        ? {
                                              width: '33%',
                                              borderRight: `0.125rem solid ${t.palette.globalSwitch.disabledBg}`,
                                              paddingLeft: '0.563rem'
                                          }
                                        : {
                                              borderBottom: `0.125rem solid ${t.palette.globalSwitch.disabledBg}`,
                                              paddingLeft: '0.75rem'
                                          }
                                }>
                                <FormControlLabel
                                    componentsProps={{ typography: { fontSize: '0.875rem' } }}
                                    label="Approve"
                                    checked={
                                        medicalReviewDeterminationValue ===
                                        MedicalReviewDeterminationConst.Approved
                                    }
                                    control={
                                        <Radio
                                            size="small"
                                            onChange={(_: unknown) => {
                                                setMedicalReviewDeterminationValue(
                                                    MedicalReviewDeterminationConst.Approved
                                                );
                                                setAnyChangeMade(true);
                                            }}
                                        />
                                    }
                                />
                            </Box>

                            <Box
                                sx={
                                    !isSmallerThanSmall
                                        ? {
                                              width: '33%',
                                              borderRight: `0.125rem solid ${t.palette.globalSwitch.disabledBg}`,
                                              paddingLeft: '0.563rem'
                                          }
                                        : {
                                              borderBottom: `0.125rem solid ${t.palette.globalSwitch.disabledBg}`,
                                              paddingLeft: '0.75rem'
                                          }
                                }>
                                <FormControlLabel
                                    componentsProps={{ typography: { fontSize: '0.875rem' } }}
                                    label="Partially Approved"
                                    checked={
                                        medicalReviewDeterminationValue ===
                                        MedicalReviewDeterminationConst.PartiallyApproved
                                    }
                                    control={
                                        <Radio
                                            size="small"
                                            onChange={(_: unknown) => {
                                                setMedicalReviewDeterminationValue(
                                                    MedicalReviewDeterminationConst.PartiallyApproved
                                                );
                                                setAnyChangeMade(true);
                                            }}
                                        />
                                    }
                                />
                            </Box>
                            <Box
                                sx={
                                    !isSmallerThanSmall
                                        ? { width: '33%', paddingLeft: '0.563rem' }
                                        : { paddingLeft: '0.75rem' }
                                }>
                                <FormControlLabel
                                    componentsProps={{ typography: { fontSize: '0.875rem' } }}
                                    label="Denied"
                                    checked={
                                        medicalReviewDeterminationValue ===
                                        MedicalReviewDeterminationConst.Denied
                                    }
                                    control={
                                        <Radio
                                            size="small"
                                            onChange={(_: unknown) => {
                                                setMedicalReviewDeterminationValue(
                                                    MedicalReviewDeterminationConst.Denied
                                                );
                                                setAnyChangeMade(true);
                                            }}
                                        />
                                    }
                                />
                            </Box>
                        </RadioGroup>
                    </Box>
                ) : (
                    <></>
                )}

                <Box sx={medicalDeterminationRationaleRowStyle}>
                    <Typography sx={medicalDeterminationRationaleTextStyle}>
                        Briefly document your medical review findings and determination for each
                        approved and denied code
                    </Typography>
                    <TextareaAutosize
                        value={reasonMedicalReviewDeterminationValue}
                        minRows={2}
                        style={{
                            minHeight: '1.25rem'
                        }}
                        onChange={(v) => {
                            handleReasonMedicalReviewDetermination(v);
                        }}
                    />
                </Box>
                <Box sx={medicalDeterminationRationaleRowStyle}>
                    <Typography sx={medicalDeterminationRationaleTextStyle}>
                        Briefly document your medical review findings and determination for each
                        approved and denied code Spanish
                    </Typography>
                    <TextareaAutosize
                        value={reasonMedicalReviewDeterminationSpanishValue}
                        minRows={2}
                        style={{
                            minHeight: '1.25rem'
                        }}
                        onChange={(v) => {
                            handleReasonMedicalReviewDeterminationSpanish(v);
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default PhysicianReviewSecondPartSpanish;
